import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Clock, ArrowRight } from 'lucide-react';
import { companyInfo, openingHours } from '../mock';

export const Footer = () => {
  return (
    <footer className="bg-[#264F77] text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 items-end">
          {/* Company Info */}
          <div>
            <img
              src="https://customer-assets.emergentagent.com/job_realestate-ai-blog/artifacts/8xaolt7j_LIVAG_Logo_2024_Entwickelt%20und%20V.webp"
              alt="LIVAG AG Logo - Immobilienentwicklung St. Gallen | Haus kaufen und verkaufen Ostschweiz"
              title="LIVAG AG - Nachhaltige Immobilienentwicklung"
              loading="lazy"
              width="300"
              height="160"
              className="h-40 w-auto mb-4"
            />
            <p className="text-sm text-gray-300 leading-relaxed">
              Immobilienentwicklung mit Zukunft – Wertschöpfung in der Ostschweiz
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-semibold mb-3">Navigation</h3>
            <ul className="space-y-0">
              <li>
                <Link to="/" className="text-sm text-gray-300 hover:text-white transition-colors inline-block leading-6" aria-label="Zur LIVAG Startseite">
                  Startseite
                </Link>
              </li>
              <li>
                <Link to="/ueber-uns" className="text-sm text-gray-300 hover:text-white transition-colors inline-block leading-6" aria-label="Über LIVAG AG erfahren">
                  Über uns
                </Link>
              </li>
              <li>
                <Link to="/projekte" className="text-sm text-gray-300 hover:text-white transition-colors inline-block leading-6" aria-label="LIVAG Immobilienprojekte ansehen">
                  Projekte
                </Link>
              </li>
              <li>
                <Link to="/blog" className="text-sm text-gray-300 hover:text-white transition-colors inline-block leading-6" aria-label="LIVAG Blog lesen">
                  Blog
                </Link>
              </li>
              <li>
                <Link to="/strategie" className="text-sm text-gray-300 hover:text-white transition-colors inline-block leading-6" aria-label="LIVAG Strategie und Werte">
                  Strategie & Werte
                </Link>
              </li>
              <li>
                <Link to="/kontakt" className="text-sm text-gray-300 hover:text-white transition-colors inline-block leading-6" aria-label="Kontakt zu LIVAG aufnehmen">
                  Kontakt
                </Link>
              </li>
              <li>
                <Link to="/immobilien-verkauf" className="text-sm text-gray-300 hover:text-white transition-colors inline-block leading-6" aria-label="Immobilie verkaufen">
                  Immobilie verkaufen
                </Link>
              </li>
              <li>
                <Link to="/immobilien-kauf" className="text-sm text-gray-300 hover:text-white transition-colors inline-block leading-6" aria-label="Immobilie kaufen">
                  Immobilie kaufen
                </Link>
              </li>
              <li>
                <Link to="/renditeobjekt-kaufen" className="text-sm text-gray-300 hover:text-white transition-colors inline-block leading-6" aria-label="Renditeobjekt kaufen">
                  Renditeobjekt kaufen
                </Link>
              </li>
              <li>
                <Link to="/renditeobjekt-verkaufen" className="text-sm text-gray-300 hover:text-white transition-colors inline-block leading-6" aria-label="Renditeobjekt verkaufen">
                  Renditeobjekt verkaufen
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Kontakt</h3>
            <ul className="space-y-3">
              <li className="flex items-start space-x-3">
                <MapPin size={18} className="text-gray-300 mt-1 flex-shrink-0" />
                <span className="text-sm text-gray-300">
                  {companyInfo.address}<br />
                  {companyInfo.city}
                </span>
              </li>
              <li className="flex items-center space-x-3">
                <Phone size={18} className="text-gray-300 flex-shrink-0" />
                <a href={`tel:${companyInfo.phone}`} className="text-sm text-gray-300 hover:text-white transition-colors">
                  {companyInfo.phone}
                </a>
              </li>
              <li className="flex items-center space-x-3">
                <Mail size={18} className="text-gray-300 flex-shrink-0" />
                <a href={`mailto:${companyInfo.email}`} className="text-sm text-gray-300 hover:text-white transition-colors">
                  {companyInfo.email}
                </a>
              </li>
            </ul>
          </div>

          {/* Opening Hours */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Öffnungszeiten</h3>
            <ul className="space-y-1">
              {openingHours.map((item) => (
                <li key={item.day} className="flex justify-between text-sm text-gray-300">
                  <span>{item.day}</span>
                  <span className={item.hours === 'Geschlossen' ? 'text-gray-400' : ''}>{item.hours}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-gray-600">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-sm text-gray-400 text-center md:text-left">
              © {new Date().getFullYear()} LIVAG AG. Alle Rechte vorbehalten.
            </p>
            <div className="flex flex-wrap justify-center gap-4 sm:gap-6">
              <Link to="/datenschutz" className="text-sm text-gray-400 hover:text-white transition-colors">
                Datenschutz
              </Link>
              <Link to="/impressum" className="text-sm text-gray-400 hover:text-white transition-colors">
                Impressum
              </Link>
              <Link to="/sitemap" className="text-sm text-gray-400 hover:text-white transition-colors" data-testid="footer-sitemap-link">
                Sitemap
              </Link>
              <Link to="/admin" className="text-sm text-gray-400 hover:text-white transition-colors">
                Admin
              </Link>
              <Link to="/tschausepp" className="text-sm text-gray-500 hover:text-white transition-colors">
                _&gt;
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
